import { Icon, IconButton, Stack, TextArea, Title, useTheme } from "@introist/react-foundation/v2";
import { useEffect, useRef, useState } from "react";

// Notes are UI-only (e.g. "Code updated") and are never sent to the assistant
export type ChatMessage = { role: "user" | "assistant" | "note"; content: string };

type ChatPaneProps = {
  messages: ChatMessage[];
  onSend: (message: string) => Promise<unknown>;
  loading: boolean;
  // Example request shown before the first message
  intro?: string;
};

const defaultIntro =
  'Describe what the transformation should do, e.g. "format the hire date as YYYY-MM-DD and leave it empty if missing".';

export const ChatPane = ({ messages, onSend, loading, intro = defaultIntro }: ChatPaneProps) => {
  const { theme } = useTheme();
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const message = draft.trim();
    if (!message || loading) return;
    setDraft("");
    await onSend(message);
  };

  return (
    <Stack
      vertical
      gap="small"
      style={{ padding: "var(--spacing-small)", boxSizing: "border-box", height: "100%" }}
    >
      <Title>AI assistant</Title>
      <Stack vertical gap="small" style={{ flex: 1, overflowY: "auto", width: "100%" }}>
        {messages.length === 0 && (
          <Title style={{ margin: "auto", textAlign: "center" }}>
            {intro} The assistant only sees field names and types, never your data.
          </Title>
        )}
        {messages.map((message, i) =>
          message.role === "note" ? (
            <Stack key={i} gap="xSmall" style={{ alignSelf: "flex-start" }}>
              <Icon name="pencil" color={theme.palette.foreground.subdued} />
              <Title style={{ color: theme.palette.foreground.subdued }}>{message.content}</Title>
            </Stack>
          ) : (
            <div
              key={i}
              style={{
                alignSelf: message.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                padding: "var(--spacing-small) var(--spacing-medium)",
                borderRadius: 8,
                background:
                  message.role === "user"
                    ? theme.palette.primary.ghosted
                    : theme.palette.surface.dimmed,
                whiteSpace: "pre-wrap"
              }}
            >
              <Title>{message.content}</Title>
            </div>
          )
        )}
        {loading && <Title>Working on it…</Title>}
        <div ref={endRef} />
      </Stack>
      <Stack gap="xSmall" alignItems="flex-end" style={{ width: "100%" }}>
        <TextArea
          rows={2}
          value={draft}
          onChange={setDraft}
          placeholder="Ask the assistant to write or change the function"
          disabled={loading}
          // Foundation TextArea is flex: 1, which would split the pane height with the message list
          style={{ flex: "1 1 auto", height: "auto" }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton
          icon="arrowRight"
          size="large"
          onClick={send}
          disabled={loading || !draft.trim()}
        />
      </Stack>
    </Stack>
  );
};
